import { useState } from "react";
import { Upload, X, BadgeCheck, Clock } from "lucide-react";
import { photoUrl, uploadFiles, formatApiError } from "../api";
import { toast } from "sonner";
import { WORK_DAYS, PROVIDER_STATUS, slugify } from "../lib/labels";

const inputCls = "w-full px-4 py-2.5 rounded-xl bg-slate-950/70 border border-white/10 text-slate-100 text-sm focus:outline-none focus:border-cyan-400/60 transition-colors";
const labelCls = "block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5";

export default function BusinessExtras({ profile, setProfile, typeLabel }) {
  const [regionInput, setRegionInput] = useState("");
  const [uploading, setUploading] = useState("");
  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const toggleDay = (d) => setProfile((p) => ({
    ...p, working_days: (p.working_days || []).includes(d)
      ? p.working_days.filter((x) => x !== d) : [...(p.working_days || []), d],
  }));
  const addRegion = () => {
    const r = regionInput.trim();
    if (r && !(profile.regions || []).includes(r)) set("regions", [...(profile.regions || []), r]);
    setRegionInput("");
  };
  const doUpload = async (files, key, single) => {
    setUploading(key);
    try {
      const paths = await uploadFiles(files);
      setProfile((p) => ({ ...p, [key]: single ? paths[0] : [...(p[key] || []), ...paths].slice(0, 6) }));
      toast.success("Dosya yüklendi — kaydetmeyi unutmayın");
    } catch (err) { toast.error(formatApiError(err)); }
    setUploading("");
  };
  const st = PROVIDER_STATUS[profile.status || "bekliyor"] || PROVIDER_STATUS.bekliyor;

  return (
    <div data-testid="business-extras" className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {typeLabel && (
          <span data-testid="business-type-badge" className="text-xs px-3 py-1.5 rounded-full border border-white/15 bg-slate-800/60 text-slate-300 font-semibold">{typeLabel}</span>
        )}
        <span data-testid="business-status-badge" className={`text-xs px-3 py-1.5 rounded-full border font-semibold ${st.cls}`}>{st.label}</span>
        {profile.verified && (
          <span data-testid="business-verified-badge" className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 font-semibold">
            <BadgeCheck className="w-3.5 h-3.5" /> Doğrulanmış İşletme
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><label className={labelCls}>İlçe</label>
          <input data-testid="biz-district" value={profile.district || ""} onChange={(e) => set("district", e.target.value)} className={inputCls} placeholder="Kadıköy" /></div>
        <div><label className={labelCls}>Çalışma Saatleri</label>
          <input data-testid="biz-working-hours" value={profile.working_hours || ""} onChange={(e) => set("working_hours", e.target.value)} className={inputCls} placeholder="08:30 - 18:30" /></div>
      </div>

      <div>
        <label className={labelCls}>Hizmet Verilen Bölgeler</label>
        <div className="flex gap-2">
          <input data-testid="biz-region-input" value={regionInput} onChange={(e) => setRegionInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addRegion())}
            className={inputCls} placeholder="İl veya ilçe ekleyin (örn. Gebze, Çayırova)" />
          <button type="button" data-testid="biz-region-add" onClick={addRegion}
            className="px-4 rounded-xl border border-cyan-400/40 text-cyan-300 text-sm font-semibold hover:bg-cyan-400/10 transition-colors whitespace-nowrap">Ekle</button>
        </div>
        {(profile.regions || []).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {profile.regions.map((r) => (
              <span key={r} data-testid={`biz-region-${slugify(r)}`}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-cyan-400/40 bg-cyan-400/10 text-cyan-300">
                {r}
                <button type="button" onClick={() => set("regions", profile.regions.filter((x) => x !== r))}><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <label className={labelCls}>Çalışma Günleri</label>
        <div className="flex flex-wrap gap-2">
          {WORK_DAYS.map((d) => (
            <button key={d} type="button" data-testid={`biz-day-${slugify(d)}`} onClick={() => toggleDay(d)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                (profile.working_days || []).includes(d) ? "border-cyan-400/60 bg-cyan-400/15 text-cyan-300" : "border-white/10 text-slate-400 hover:text-slate-200"}`}>
              {d}
            </button>
          ))}
        </div>
        <label data-testid="biz-247" className="flex items-center gap-2 cursor-pointer mt-3">
          <input type="checkbox" checked={!!profile.is_247} onChange={(e) => set("is_247", e.target.checked)} className="accent-cyan-400 w-4 h-4" />
          <span className="text-sm text-slate-300 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-cyan-300" /> 7/24 hizmet veriyorum</span>
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><label className={labelCls}>WhatsApp</label>
          <input data-testid="biz-whatsapp" value={profile.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} className={inputCls} placeholder="0 5xx xxx xx xx" /></div>
        <div><label className={labelCls}>Web Sitesi</label>
          <input data-testid="biz-website" value={profile.website || ""} onChange={(e) => set("website", e.target.value)} className={inputCls} placeholder="https://" /></div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[["logo", "İşletme Logosu", true], ["photos", "İşletme Fotoğrafları", false], ["tax_document", "Vergi Levhası", true]].map(([key, label, single]) => (
          <div key={key}>
            <label data-testid={`biz-upload-${key}`}
              className="flex flex-col items-center justify-center gap-1.5 px-3 py-4 rounded-xl border border-dashed border-white/20 text-slate-400 text-xs cursor-pointer hover:border-cyan-400/50 hover:text-cyan-300 transition-colors">
              <Upload className="w-4 h-4" />
              {uploading === key ? "Yükleniyor..." : label}
              <input type="file" accept="image/*" multiple={!single} className="hidden"
                onChange={async (e) => { await doUpload([...e.target.files], key, single); e.target.value = ""; }} />
            </label>
            {(single ? (profile[key] ? [profile[key]] : []) : (profile[key] || [])).map((p) => (
              <div key={p} className="relative inline-block mt-2 mr-1.5">
                <img src={photoUrl(p)} alt="" className="w-14 h-10 object-cover rounded-lg border border-white/10" />
                <button type="button" onClick={() => single ? set(key, "") : set(key, profile[key].filter((x) => x !== p))}
                  className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center">
                  <X className="w-2.5 h-2.5" />
                </button>
              </div>
            ))}
          </div>
        ))}
      </div>
      {key_note(profile)}
    </div>
  );
}

function key_note(profile) {
  if (profile.verified) return null;
  return (
    <p className="text-xs text-slate-500 flex items-start gap-1.5">
      <BadgeCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-400" />
      {profile.tax_document
        ? "Vergi levhanız yüklü — yönetici onayı sonrası 'Doğrulanmış İşletme' rozeti verilir."
        : "Vergi levhası zorunlu değildir; yüklerseniz onay sonrası 'Doğrulanmış İşletme' rozeti alırsınız. Belgeniz yalnızca yönetici tarafından görülür."}
    </p>
  );
}
